import React, { useState, useMemo } from 'react';
import { useForm, FormProvider, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, ArrowRight, X, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PhotoThumb } from '@/components/PhotoThumb';
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Stepper } from '../components/Stepper';
import { ProgressBar } from '../components/ProgressBar';
import { FormSectionRenderer } from '../components/FormSectionRenderer';
import { generateZodSchema } from '../utils/schemaGenerator';
import { useLookupsSnapshot, toOptionLabel } from '@/features/lookups/snapshot';
import { useAuth } from '@/hooks/useAuth';
import type { AdoptionFormTemplate, AdopterProfile } from '../types';

interface AdoptionWizardProps {
    template: AdoptionFormTemplate;
    existingProfile?: AdopterProfile | null;
    selectedAnimal?: any | null; // Animal selecionado pelo usuário
    onComplete: (data: any) => void;
    onSaveDraft?: (data: any) => void;
    isSubmitting?: boolean;
}

export function AdoptionWizard({
    template,
    existingProfile,
    selectedAnimal,
    onComplete,
    onSaveDraft,
    isSubmitting = false,
}: AdoptionWizardProps) {
    const { toast } = useToast();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [currentStep, setCurrentStep] = useState(0);
    const { data: lookupSnapshot } = useLookupsSnapshot();

    // Generar schema de validación dinámico + aceptación final obligatoria
    const schema = useMemo(() => {
        const baseSchema = generateZodSchema(template.schemaJson);
        return baseSchema.extend({
            review_acceptance: z.literal(true, {
                errorMap: () => ({
                    message: 'Debes aceptar para continuar',
                }),
            }),
            tracking_authorization: z.literal(true, {
                errorMap: () => ({
                    message: 'Debes autorizar el seguimiento para continuar',
                }),
            }),
        });
    }, [template]);

    // Generar valores por defecto para todos los campos del formulario
    const getDefaultValues = useMemo(() => {
        const defaults: Record<string, any> = {};

        // Inicializar todos los campos con valores por defecto
        template.schemaJson.sections.forEach(section => {
            section.fields.forEach(field => {
                if (field.type === 'checkbox') {
                    defaults[field.id] = false;
                } else if (field.type === 'range') {
                    defaults[field.id] = [0];
                } else if (field.type === 'number') {
                    defaults[field.id] = undefined;
                } else {
                    defaults[field.id] = '';
                }
            });
        });

        return {
            ...defaults,
            ...(existingProfile ? mapProfileToForm(existingProfile) : {}),
            ...(selectedAnimal ? mapAnimalToPreferences(selectedAnimal) : {}),
            review_acceptance: false,
            tracking_authorization: false,
        };
    }, [template, existingProfile, selectedAnimal]);

    // Inicializar formulario
    const methods = useForm({
        resolver: zodResolver(schema),
        mode: 'onSubmit', // Solo validar al enviar, no en cada cambio
        reValidateMode: 'onChange', // Después del primer submit, revalidar en cambios
        defaultValues: getDefaultValues,
    });

    const { trigger, handleSubmit, getValues, formState: { isValid } } = methods;

    const sections = template.schemaJson.sections;
    const totalSteps = sections.length + 1; // + resumen
    const summaryStepIndex = totalSteps - 1;
    const currentSection = sections[currentStep];

    // Steps para el componente Stepper
    const steps = [
        ...sections.map((section, index) => ({
            label: section.label || section.title || section.id,
            completed: index < currentStep,
            active: index === currentStep,
        })),
        {
            label: 'Resumen',
            completed: currentStep > summaryStepIndex,
            active: currentStep === summaryStepIndex,
        },
    ];

    // Manejadores de navegación
    const handleNext = async () => {
        // Validar solo los campos de la sección actual
        const fieldsInCurrentSection = currentSection?.fields.map(f => f.id) || [];
        const isStepValid = await trigger(fieldsInCurrentSection);

        if (isStepValid) {
            setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            toast({
                variant: "destructive",
                title: "Campos incompletos",
                description: "Por favor, completa todos los campos requeridos antes de continuar.",
            });
        }
    };

    const handleBack = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancel = () => {
        navigate('/adopt');
    };

    const onSubmit = (data: any) => {
        onComplete(data);
    };

    return (
        <div className="space-y-8">
            {selectedAnimal && (() => {
                const photoUrl = selectedAnimal.photos?.[0]?.url || selectedAnimal.primaryPhotoUrl || selectedAnimal.photo;
                const resolveLookup = (collection: any[] | undefined, id?: number) => {
                    if (!collection || id == null) return undefined;
                    const found = collection.find((i: any) => Number(i.id) === Number(id));
                    return found ? toOptionLabel(found) : undefined;
                };
                const resolveLabel = (value?: string, altValue?: string, lookupValue?: string) => value || altValue || lookupValue || '—';
                const sexLabel = resolveLabel(
                    selectedAnimal.sexLabel || selectedAnimal.sex_label,
                    selectedAnimal.sex?.label || selectedAnimal.sex?.name,
                    resolveLookup(lookupSnapshot?.sexes, selectedAnimal.sexId ?? selectedAnimal.sex_id),
                );
                const breedLabel = resolveLabel(
                    selectedAnimal.breedLabel || selectedAnimal.breed_label,
                    selectedAnimal.breed?.label || selectedAnimal.breed?.name,
                    resolveLookup(lookupSnapshot?.breeds, selectedAnimal.breedId ?? selectedAnimal.breed_id),
                );
                const colorLabel = resolveLabel(
                    selectedAnimal.colorLabel || selectedAnimal.color_label,
                    selectedAnimal.color?.label || selectedAnimal.color?.name,
                    resolveLookup(lookupSnapshot?.colors, selectedAnimal.colorId ?? selectedAnimal.color_id),
                );
                const sizeLabel = resolveLabel(
                    selectedAnimal.sizeLabel || selectedAnimal.size_label,
                    selectedAnimal.size?.label || selectedAnimal.size?.name,
                    resolveLookup(lookupSnapshot?.sizes, selectedAnimal.sizeId ?? selectedAnimal.size_id),
                );
                return (
                    <div className="rounded-xl border bg-muted/40 p-4">
                        <div className="flex flex-col sm:flex-row gap-4">
                            {photoUrl && (
                                <PhotoThumb
                                    src={/^https?:\/\//i.test(photoUrl) ? photoUrl : `${import.meta.env.VITE_API_URL}${photoUrl}`}
                                    alt={selectedAnimal.name}
                                    className="w-24 h-24 rounded-lg"
                                />
                            )}
                            <div className="flex-1">
                                <p className="text-xs uppercase text-muted-foreground">Animal seleccionado</p>
                                <h3 className="text-xl font-semibold">{selectedAnimal.name}</h3>
                                <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <span className="text-muted-foreground">Sexo: </span>
                                        <span className="font-medium">{sexLabel}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Raza: </span>
                                        <span className="font-medium">{breedLabel}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Color: </span>
                                        <span className="font-medium">{colorLabel}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Tamaño: </span>
                                        <span className="font-medium">{sizeLabel}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
            {/* Progreso */}
            <div className="space-y-6">
                <Stepper
                    steps={steps}
                    currentStep={currentStep}
                    className="mb-8"
                />
                <ProgressBar
                    current={currentStep + 1}
                    total={totalSteps}
                    showLabel={false}
                    className="h-1"
                />
            </div>

            {/* Formulario */}
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                    {(existingProfile || user) && (
                        <div className="rounded-xl border bg-card p-4">
                            <p className="text-xs uppercase text-muted-foreground">Datos del adoptante</p>
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                                <div>
                                    <p className="text-muted-foreground">Nombre</p>
                                    <p className="font-medium">
                                        {existingProfile?.user?.full_name ||
                                            existingProfile?.user?.fullName ||
                                            [existingProfile?.firstName, existingProfile?.lastName].filter(Boolean).join(' ') ||
                                            (user as any)?.full_name ||
                                            (user as any)?.fullName ||
                                            '—'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Email</p>
                                    <p className="font-medium">{existingProfile?.email || existingProfile?.user?.email || (user as any)?.email || '—'}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Teléfono</p>
                                    <p className="font-medium">{existingProfile?.phone || existingProfile?.user?.phone || (user as any)?.phone || '—'}</p>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="min-h-[400px]">
                        {currentStep < summaryStepIndex ? (
                            <FormSectionRenderer
                                section={currentSection}
                                selectedAnimal={null}
                            />
                        ) : (
                            <SummaryStep
                                sections={sections}
                                values={getValues()}
                                control={methods.control}
                                isSubmitting={isSubmitting}
                                lookupSnapshot={lookupSnapshot}
                                selectedAnimal={selectedAnimal}
                            />
                        )}
                    </div>

                    {/* Botones de acción */}
                    <div className="flex flex-col sm:flex-row justify-between gap-4 pt-6 border-t">
                        <div className="order-2 sm:order-1">
                            {currentStep > 0 ? (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleBack}
                                    disabled={isSubmitting}
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Anterior
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleCancel}
                                    disabled={isSubmitting}
                                >
                                    <X className="h-4 w-4 mr-2" />
                                    Cancelar
                                </Button>
                            )}
                        </div>

                        <div className="order-1 sm:order-2 flex gap-3">
                            {currentStep < totalSteps - 1 ? (
                                <Button
                                    type="button"
                                    onClick={handleNext}
                                    disabled={isSubmitting}
                                >
                                    Siguiente
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            ) : (
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="bg-green-600 hover:bg-green-700"
                                >
                                    Confirmar y Enviar
                                    <CheckCircle2 className="ml-2 h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                </form>
            </FormProvider>
        </div>
    );
}

// Helper para mapear perfil existente a valores del formulario
// Esto es simplificado, idealmente el binding debería manejar esto automáticamente
function mapProfileToForm(profile: AdopterProfile): Record<string, any> {
    const values: Record<string, any> = {};

    // Mapeo manual básico basado en los campos conocidos
    const cityValue = getProfileCityValue(profile);
    if (cityValue !== undefined) values.city = cityValue;
    if (profile.address) values.address = profile.address;
    if (profile.housingType) values.housing_type = profile.housingType;
    if (profile.housingOwnership) values.housing_ownership = profile.housingOwnership;

    return values;
}

function getProfileCityValue(profile: AdopterProfile) {
    const candidates = [
        profile.cityId,
        profile.city_id,
        typeof profile.city === 'object' && profile.city ? (profile.city as any).id : undefined,
        profile.city,
    ];

    for (const candidate of candidates) {
        if (candidate === null || candidate === undefined) continue;
        if (typeof candidate === 'number' && !Number.isNaN(candidate)) return candidate;
        if (typeof candidate === 'string' && candidate.trim()) {
            const numeric = Number(candidate.trim());
            if (!Number.isNaN(numeric)) return numeric;
            return candidate;
        }
    }
    return undefined;
}

function SummaryStep({
    sections,
    values,
    control,
    isSubmitting,
    lookupSnapshot,
    selectedAnimal,
}: {
    sections: AdoptionFormTemplate['schemaJson']['sections'];
    values: Record<string, any>;
    control: Control<any>;
    isSubmitting?: boolean;
    lookupSnapshot: ReturnType<typeof useLookupsSnapshot>['data'];
    selectedAnimal?: any | null;
}) {
    const getLookupLabel = (lookupType: string | undefined, raw: any) => {
        if (!lookupType || !lookupSnapshot) return undefined;
        const id = Number(raw);

        const getCollection = () => {
            switch (lookupType) {
                case 'species': return lookupSnapshot.species;
                case 'sex': return lookupSnapshot.sexes;
                case 'size': return lookupSnapshot.sizes;
                case 'breed': return lookupSnapshot.breeds;
                case 'color': return lookupSnapshot.colors;
                case 'status': return lookupSnapshot.statuses;
                default: return undefined;
            }
        };

        const collection = getCollection();
        const item = collection?.find((i: any) => Number(i.id) === id);
        return item ? toOptionLabel(item) : undefined;
    };

    const renderValue = (field: any) => {
        const raw = values[field.id];
        if (raw === undefined || raw === null || raw === '') return 'Sin responder';
        if (Array.isArray(raw)) return raw.join(' - ');
        if (typeof raw === 'boolean') return raw ? 'Sí' : 'No';

        if (field.type === 'city') {
            const id = Number(raw);
            const city = lookupSnapshot?.cities?.find((c: any) => Number(c.id) === id);
            if (city) {
                const label = city.label || city.name || city.title || city.city;
                const dept = city.department || city.state || city.region;
                return label ? (dept ? `${label} - ${dept}` : label) : String(raw);
            }
        }

        const match = field.options?.find((opt: any) => String(opt.value) === String(raw));
        if (match) return match.label;

        if (field.lookupType) {
            const lookupLabel = getLookupLabel(field.lookupType, raw);
            if (lookupLabel) return lookupLabel;
        }

        if (field.type === 'radio' && (raw === 'true' || raw === 'false')) {
            return raw === 'true' ? 'Sí' : 'No';
        }

        return String(raw);
    };

    const isFieldVisible = (field: any) => {
        if (!field.conditional) return true;
        const dependentValue = values[field.conditional.field];
        const { operator, value } = field.conditional;
        switch (operator) {
            case '==': return dependentValue === value;
            case '!=': return dependentValue !== value;
            case '>': return dependentValue > value;
            case '<': return dependentValue < value;
            case 'in': return Array.isArray(value) && value.includes(dependentValue);
            case 'contains': return Array.isArray(dependentValue) && dependentValue.includes(value);
            default: return true;
        }
    };

    return (
        <div className="space-y-8">
            <div className="space-y-2">
                <h2 className="text-2xl font-semibold text-foreground">Revisar y confirmar</h2>
                <p className="text-muted-foreground">
                    Verifica que tus respuestas estén correctas antes de enviar el formulario.
                </p>
            </div>

            <div className="space-y-6">
                {sections.map((section) => (
                    <div key={section.id} className="border rounded-lg p-4 space-y-4">
                        <h3 className="font-semibold text-lg">
                            {section.label || section.title || section.id}
                        </h3>
                        <div className="grid gap-3 sm:grid-cols-2">
                            {section.fields
                                .filter((field) => isFieldVisible(field))
                                .map((field) => (
                                    <div key={field.id} className="text-sm">
                                        <p className="text-muted-foreground">
                                            {field.type === 'city' ? 'Ciudad / Departamento' : field.label}
                                        </p>
                                        <p className="font-medium">{renderValue(field)}</p>
                                    </div>
                                ))}
                        </div>
                    </div>
                ))}
            </div>

                            <div className="space-y-3 rounded-lg border p-4 bg-muted/40">
                <p className="text-sm text-foreground">
                    Es importante que comprenda que la adopción es una responsabilidad para toda la vida y que el animal
                    que va a acoger ha sido rescatado en condición de calle, por lo que puede tener condiciones especiales
                    en su adaptación. El compromiso es el amor y responsabilidad que usted  y su familia va a entregarle
                    como nuevo miembro del hogar.
                </p>

                                <FormField
                                    control={control}
                                    name="review_acceptance"
                                    render={({ field }) => (
                                        <FormItem className="flex items-start space-x-3 space-y-0">
                            <FormControl>
                                <Checkbox
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                    disabled={isSubmitting}
                                    type="button"
                                />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                                <FormLabel className="font-medium">
                                    Estoy de acuerdo con estas apreciaciones
                                </FormLabel>
                                <FormMessage />
                            </div>
                        </FormItem>
                    )}
                                />

                                <FormField
                                    control={control}
                                    name="tracking_authorization"
                                    render={({ field }) => (
                                        <FormItem className="flex items-start space-x-3 space-y-0 pt-4">
                                            <FormControl>
                                                <Checkbox
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                    disabled={isSubmitting}
                                                    type="button"
                                                />
                                            </FormControl>
                                            <div className="space-y-1 leading-none">
                                                <FormLabel className="font-medium text-justify block">
                                                    Autorizo a la entidad, a la cual hago esta solicitud de adopción, para que realice de manera presencial, virtual o telefónica, seguimiento al animal de compañía durante el tiempo que considere pertinente. Lo anterior con el propósito de acompañar a la familia adoptante en el proceso de adaptación y garantizar que el animal reciba los cuidados básicos que garanticen su bienestar.
                                                </FormLabel>
                                                <FormMessage />
                                            </div>
                                        </FormItem>
                                    )}
                                />

                            </div>
        </div>
    );
}

// Helper para mapear datos del animal a preferencias del formulario
function mapAnimalToPreferences(animal: any): Record<string, any> {
    const values: Record<string, any> = {};

    if (animal.speciesId) values.preferred_species_id = animal.speciesId;
    if (animal.sizeId) values.preferred_size_id = animal.sizeId;
    if (animal.sexId) values.preferred_sex_id = animal.sexId;
    if (animal.breedId) values.preferred_breed_id = Number(animal.breedId);
    if (animal.colorId) values.preferred_color_id = animal.colorId;

    // Edad en meses (campo correcto del API)
    if (animal.approxAgeMonths !== undefined) {
        values.age_range = [animal.approxAgeMonths];
    }

    return values;
}
